// How a tie on record was settled, one row at a time (scenario H1, B-457).
//
// STORED AS WHO AND BY WHAT, NOT AS A SENTENCE. The standings write knows
// which of the league's tiebreakers put a team ahead of the one level with it
// just below (`standings.tiebreak_note`); the read words it with that team's
// name as it is now, so a rename never leaves a stale name in the table.
//
// The rule is the one that separated the two, which a tie of three or more
// may make a different rule for each pair: the group is settled together, in
// the league's order of tiebreakers, the teams one puts on top placed and the
// rest starting the list again, and each row says what put it ahead of the
// row below (B-467).

import { z } from 'zod';
import { TiebreakerId } from '../rules/schemas';

/** `standings.tiebreak_note`: the team this one is ahead of, and the tiebreaker that did it. */
export const TiebreakNote = z.object({
  over: z.string().min(1),
  by: TiebreakerId,
});
export type TiebreakNote = z.infer<typeof TiebreakNote>;

const BY: Record<TiebreakNote['by'], string> = {
  'head-to-head': 'on head-to-head record',
  'points-for': 'on points scored',
  'points-against': 'on fewer points allowed',
  'division-record': 'on division record',
  'record-vs-common': 'on record against common opponents',
  'all-play-record': 'on all-play record',
  'points-for-last-3': 'on points scored over the last three weeks',
  'coin-flip': 'by a coin flip',
};

/**
 * The row's sentence: "Ahead of Pylon Pushers on points scored."
 *
 * ON POINTS THAT LOOK LEVEL, THE POINTS (the audit of 2026-10-20). A score is
 * kept to the hundredth and shown to a tenth, so two totals the table shows
 * as 1234.5 can still differ, and the row said it was ahead on points beside
 * two equal numbers. When the rule is points scored or allowed and the two
 * figures read the same to a tenth, the sentence gives them to the hundredth:
 * "Ahead of Pylon Pushers on points scored, 1234.54 to 1234.51."
 */
export function tiebreakSentence(
  note: TiebreakNote,
  overName: string,
  figures?: { mine: number; theirs: number },
): string {
  // AS THE TABLE WRITES THEM, `toFixed(1)`: rounding by hand disagrees with
  // it on a total ending in .x5 stored a hair under (the third review).
  const close =
    figures !== undefined &&
    (note.by === 'points-for' || note.by === 'points-against') &&
    figures.mine.toFixed(1) === figures.theirs.toFixed(1);
  return close
    ? `Ahead of ${overName} ${BY[note.by]}, ${figures.mine.toFixed(2)} to ${figures.theirs.toFixed(2)}.`
    : `Ahead of ${overName} ${BY[note.by]}.`;
}

/**
 * The sentence without its figures, for Simple, which removes numbers and
 * adds no words (DETAIL_LEVELS.md): "Ahead of Pylon Pushers on points scored."
 */
export function tiebreakWords(sentence: string): string {
  return sentence.replace(/, -?[\d.]+ to -?[\d.]+\.$/, '.');
}
